import { Request } from "@xt/xtbase";

const { XtAxios4 } = Request;

/** OpenAPI：GET /prop/leaderboard（stage=StageEnum, period=DAY|WEEK|MONTH） */
const URL = "/sapi/v4/prop-trading/prop/";

export interface PropLeaderboardItemVO {
  rank: number;
  userId: number;
  profit: number;
  profitPercent: number;
  rewardAmount: number;
}

export interface PropLeaderboardVO {
  list: PropLeaderboardItemVO[];
  selfRank?: PropLeaderboardItemVO | null;
  totalCount?: number;
}

export function get_prop_leaderboard(config?) {
  return XtAxios4.get(URL + "leaderboard", config);
}

export interface PropStageRulesVO {
  profitTargetPercent?: string;
  maxDrawdownPercent?: string;
  durationHours?: number;
}

export interface PropStageTicketReviveVO {
  price?: string | null;
  currencyId?: number | null;
  count?: number | null;
}

/** OpenAPI：GET /prop/home */
export interface PropStageItemVO {
  stage?: string;
  stageName?: string;
  canJoin?: boolean;
  /** 国际化 key，如 prop.reason.need_elite_first */
  cannotJoinReason?: string;
  /** 挑战卡展示：利润目标、回撤、时限 */
  rules?: PropStageRulesVO | null;
  /** 挑战卡报名费用展示 */
  ticket?: PropStageTicketReviveVO | null;
}

export interface PropLiveStageSummaryVO {
  stage?: string;
  stageName?: string;
  liveAmount?: string;
  durationHours?: number;
}

export interface PropHomeVO {
  /** 当前进行中场次；无进行中为 null */
  userStage?: string | null;
  /** CHALLENGING / PENDING_REVIVE 等；无进行中为 null */
  userStatus?: string | null;
  currentStageName?: string | null;
  remainingSeconds?: number;
  reviveCountLeft?: number;
  /** 当前场次门票/订单摘要，无则为 null */
  currentStageTicket?: Record<string, unknown> | null;
  canJoinLive?: boolean;
  stages?: PropStageItemVO[];
  liveStage?: PropLiveStageSummaryVO | null;
  propSimulateCurrencyCode?: string;
  propSimulateCurrencyId?: number;
}

export function get_prop_home(config?) {
  return XtAxios4.get(URL + "home", config);
}

/** OpenAPI：GET /prop/trading/context — 交易页上下文（CFD 使用 accountId、symbolSuffix 等） */
export interface PropTradingContextVO {
  /** 当前场次；无进行中时为 null */
  stage?: string | null;
  stageName?: string | null;
  remainingSeconds?: number;
  /** 剩余复活次数；实盘场时为 0 或不返回 */
  reviveCountLeft?: number;
  /** CHALLENGING / PENDING_REVIVE */
  status?: string | null;
  canTrade?: boolean;
  /** CFD 请求统一使用该 accountId */
  accountId?: number;
  /** 报价币种；模拟盘与实盘均使用 USDX */
  quoteCurrency?: string | null;
  /**
   * 当前可交易余额（报价币，与 quoteCurrency 一致，如 USDX）。
   * 后端字段名可能是 balance / availableBalance，前端二者兼容。
   */
  balance?: string | number | null;
  availableBalance?: string | number | null;
  /** symbol 后缀，如 _usdx_cfd、_usdt_cfd */
  symbolSuffix?: string | null;
  propSimulateCurrencyCode?: string | null;
  propSimulateCurrencyId?: number;
}

export function get_prop_trading_context(config?) {
  return XtAxios4.get(URL + "trading/context", config);
}

/** OpenAPI：POST /prop/trading/positions/open — Prop 开仓（校验场次杠杆后调 CFD） */
export interface PropOpenPositionReq {
  /** 如 BTC_USDX */
  symbol: string;
  /** 1=买(LONG), 2=卖(SHORT) */
  side: number;
  quantity: number;
  /** 1=限价, 2=市价 */
  priceType?: number;
  /** 限价必填 */
  price?: number;
  leverage: number;
  /** 0=关闭过夜, 1=开启 */
  keep?: number;
  takeProfitPrice?: number;
  stopLossPrice?: number;
}

/** 前端 CFD 交易对（如 btc_usdt_cfd）→ 通用 symbol（如 BTC_USDT），与交易对报价一致 */
export function prop_cfd_symbol_to_open_api(cfdSymbol: string): string {
  const lowered = String(cfdSymbol ?? "").trim().toLowerCase();
  const core = lowered.endsWith("_cfd") ? lowered.slice(0, -4) : lowered;
  return core
    .split("_")
    .filter(Boolean)
    .map((seg) => seg.toUpperCase())
    .join("_");
}

/**
 * 仅 Prop 交易页（/proptrading/trade）开仓：接口报价固定 USDX，避免 btc_usdt_cfd 误传 BTC_USDT。
 * 基础币取自交易对第一段，如 btc_* → BTC_USDX。
 */
export function prop_cfd_symbol_to_open_api_for_prop_trading_page(cfdSymbol: string): string {
  const lowered = String(cfdSymbol ?? "").trim().toLowerCase();
  const core = lowered.endsWith("_cfd") ? lowered.slice(0, -4) : lowered;
  const base = core.split("_").filter(Boolean)[0] || "btc";
  return `${base.toUpperCase()}_USDX`;
}

export function build_prop_open_position_body(
  cfdMarketSymbol: string,
  fields: {
    side: number;
    quantity: number;
    leverage: number;
    priceType?: number;
    price?: number;
    keep?: number;
    takeProfitPrice?: number;
    stopLossPrice?: number;
  },
  options?: { propTradingPage?: boolean }
): PropOpenPositionReq {
  const priceType = fields.priceType ?? 2;
  const symbol = options?.propTradingPage
    ? prop_cfd_symbol_to_open_api_for_prop_trading_page(cfdMarketSymbol)
    : prop_cfd_symbol_to_open_api(cfdMarketSymbol);
  const body: PropOpenPositionReq = {
    symbol,
    side: fields.side,
    quantity: fields.quantity,
    leverage: fields.leverage,
    priceType,
    keep: fields.keep ?? 0,
  };
  if (priceType === 1 && fields.price != null && Number(fields.price) > 0) {
    body.price = Number(fields.price);
  }
  if (fields.takeProfitPrice != null && Number(fields.takeProfitPrice) > 0) {
    body.takeProfitPrice = Number(fields.takeProfitPrice);
  }
  if (fields.stopLossPrice != null && Number(fields.stopLossPrice) > 0) {
    body.stopLossPrice = Number(fields.stopLossPrice);
  }
  return body;
}

export function post_prop_open_position(config?) {
  return XtAxios4.post(URL + "trading/positions/open", null, config);
}

/** OpenAPI：GET /prop/account/overview — 账户概览（模拟盘） */
export interface PropAccountOverviewVO {
  stage?: string | null;
  stageName?: string | null;
  /** 与当前账户场次绑定，收益兑换可选传 */
  userStageId?: number | null;
  /** CHALLENGING / PENDING_REVIVE / COMPLETED / FAILED */
  userStatus?: string | null;
  balance?: number;
  profit?: number;
  profitPercent?: number;
  reviveCountLeft?: number;
  rank?: number;
  totalCount?: number;
  progressToLivePercent?: number;
  inviteCode?: string | null;
  totalCommission?: number;
  /** 如 EXCHANGE、REVIVE */
  actions?: string[] | null;
  maxExchangeableAmount?: number;
  exchangedAmount?: number;
}

export function get_prop_account_overview(config?) {
  return XtAxios4.get(URL + "account/overview", config);
}

/** OpenAPI：GET /prop/account/live/overview — 实盘账户概览（未进入过实盘时可能 404） */
export interface PropLiveAccountVO {
  liveBalance?: number;
  profit?: number;
  dividend?: number;
  rank?: number;
  expired?: boolean;
  expireAt?: string;
  remainingSeconds?: number;
  userStageId?: number;
}

export function get_prop_account_live_overview(config?) {
  return XtAxios4.get(URL + "account/live/overview", config);
}

/** OpenAPI：GET /prop/account/balance-curve?days= — 资产曲线（USDX） */
export interface PropUserBalanceCurvePointVO {
  date?: string;
  balance?: string;
  profit?: string;
  profitPercent?: string;
}

export interface PropUserBalanceCurveStatisticsVO {
  maxBalance?: string;
  minBalance?: string;
  maxProfitPercent?: string;
  minProfitPercent?: string;
  initialBalance?: string;
  currentBalance?: string;
  totalProfit?: string;
}

export interface PropUserBalanceCurveVO {
  dataPoints?: PropUserBalanceCurvePointVO[] | null;
  statistics?: PropUserBalanceCurveStatisticsVO | null;
}

export function get_prop_account_balance_curve(config?) {
  return XtAxios4.get(URL + "account/balance-curve", config);
}

/** OpenAPI：GET /prop/account/partner — 伙伴计划 */
export interface PropPartnerFriendVO {
  userId?: number;
  userNameMasked?: string | null;
  /** 已注册 / 已购票 等，后端文案或枚举 */
  status?: string | null;
  relatedAt?: string | null;
}

export interface PropPartnerVO {
  inviteCode?: string | null;
  totalCommission?: number;
  friends?: PropPartnerFriendVO[] | null;
}

export function get_prop_account_partner(config?) {
  return XtAxios4.get(URL + "account/partner", config);
}

/**
 * OpenAPI：POST /prop/account/exchange — 收益兑换
 * @see Apifox client/Prop 账户 — 收益兑换
 */
export interface PropAccountExchangeReq {
  /** 要兑换的 USDX 金额，须 > 0 */
  amount: number;
  /** 接受的比例（可选，用于确认阶梯），如 0.25 表示 25% */
  acceptRatio?: number;
  /** 指定 COMPLETED 场次；不传则后端取最近一条 COMPLETED */
  userStageId?: number;
}

/** POST /prop/account/exchange 响应 */
export interface PropExchangeVO {
  exchangeRecordId?: number;
  userStageId?: number;
  stage?: string;
  usdxAmount?: number;
  exchangeRate?: number;
  realAmount?: number;
  realCurrencyId?: number;
  createdAt?: string;
}

export function post_prop_account_exchange(body: PropAccountExchangeReq, config?) {
  return XtAxios4.post(URL + "account/exchange", body, config);
}

/** OpenAPI：GET /prop/account/exchange-records — 兑换记录分页 */
export interface PropExchangeRecordVO {
  userStageId?: number;
  stage?: string;
  stageName?: string;
  usdxAmount?: number;
  exchangeRate?: number;
  realAmount?: number;
  realCurrencyCode?: string;
  createdAt?: string;
  outBillId?: number;
  inBillId?: number;
}

export interface CountPagePropExchangeRecordVO {
  items?: PropExchangeRecordVO[] | null;
  totalCount?: number;
  pageCount?: number;
}

export function get_prop_account_exchange_records(config?) {
  return XtAxios4.get(URL + "account/exchange-records", config);
}

/** OpenAPI：GET /prop/stages/{stage}/detail */
export interface PropStageCapitalVO {
  simulateAmount?: string;
  liveAmount?: string;
  currencyId?: number;
  restartLinePercent?: string;
  maxLeverage?: number;
}

export interface PropStageExchangeTierVO {
  minProfitPercent?: string;
  maxProfitPercent?: string;
  exchangePercent?: string;
  minExchangeAmount?: string;
}

export interface PropStageProfitShareTierVO {
  minProfitPercent?: string;
  maxProfitPercent?: string;
  userSharePercent?: string;
}

/** rankRewards.rewards 单项 */
export interface PropRankRewardTierVO {
  rankStart?: number;
  rankEnd?: number;
  amount?: string;
  currency?: string;
}

/** GET /prop/stages/{stage}/detail 的 rankRewards */
export interface PropStageRankRewardsVO {
  periods?: string[];
  fakeBaseCount?: number;
  rewards?: PropRankRewardTierVO[] | null;
}

export interface PropStageDetailVO {
  stage?: string;
  stageName?: string;
  capital?: PropStageCapitalVO;
  rules?: PropStageRulesVO;
  ticket?: PropStageTicketReviveVO | null;
  revive?: PropStageTicketReviveVO | null;
  exchangeTiers?: PropStageExchangeTierVO[] | null;
  profitShareTiers?: PropStageProfitShareTierVO[] | null;
  /** 排名说明 {amount}：取 rewards[0].amount（+ currency） */
  rankRewards?: PropStageRankRewardsVO | null;
  reentryAfterFail?: boolean;
  reentryAfterSuccess?: boolean;
  canJoin?: boolean;
  /** 不可参加时的 i18n key，如 prop.reason.need_elite_first */
  cannotJoinReason?: string;
}

export function get_prop_stage_detail(stage: string, config?) {
  const s = encodeURIComponent(String(stage).trim());
  return XtAxios4.get(`${URL}stages/${s}/detail`, config);
}

/** POST /prop/ticket/orders — 创建门票/复活订单或进入实盘 */
export interface CreatePropTicketOrderReq {
  stage: string;
  /** true=复活；门票报名为 false 或不传；实盘场忽略 */
  revive?: boolean;
  /** 模拟场次必填：支付币种 */
  payCurrencyId?: number;
  /** 模拟场次必填：支付金额（字符串，与后端校验一致） */
  payAmount?: string;
}

export interface PropTicketOrderVO {
  orderId?: number;
  orderNo?: string;
  orderType?: string;
  stage?: string;
  stageName?: string;
  userStageId?: number;
  payCurrencyId?: number;
  payAmount?: number;
  payStatus?: string;
  creditAmount?: number;
  paidAt?: string;
  expireAt?: string;
  createdAt?: string;
  /** 待支付时跳转 */
  paymentUrl?: string;
}

/** OpenAPI：GET /prop/ticket/orders 分页项 */
export interface CountPagePropTicketOrderVO {
  items?: PropTicketOrderVO[] | null;
  totalCount?: number;
  pageCount?: number;
}

export interface PropLiveJoinVO {
  stage?: string;
  userStageId?: number;
  expireAt?: string;
  success?: boolean;
}

export interface CreatePropOrderResultVO {
  resultType: string;
  ticketOrder?: PropTicketOrderVO | null;
  liveJoin?: PropLiveJoinVO | null;
}

export function post_prop_ticket_order(body: CreatePropTicketOrderReq, config?) {
  return XtAxios4.post(URL + "ticket/orders", body, config);
}

/** OpenAPI：GET /prop/ticket/orders — 门票/复活订单列表（分页），query: page, pageSize, orderType?, stage? */
export function get_prop_ticket_orders(config?) {
  return XtAxios4.get(URL + "ticket/orders", config);
}
